import { useState } from 'react';
import {
  App,
  Badge,
  Button,
  Card,
  Input,
  Popconfirm,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  CheckCircleOutlined,
  ClusterOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
  ReloadOutlined,
  ClearOutlined,
  SafetyCertificateOutlined,
  SearchOutlined,
  StopOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  useDeactivateMdm,
  useMdms,
  useReactivateMdm,
  useValidateMdmCredentials,
  useClearAmapiCache,
} from '@/hooks/useMdms';
import type { MdmDto } from '@/types/mdm';
import { MdmFormModal } from './MdmFormModal';

const { Title, Text } = Typography;

export function MdmsPage() {
  const { message, modal } = App.useApp();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<MdmDto | null>(null);

  const { data, isLoading, refetch, isFetching } = useMdms({
    page,
    pageSize,
    search: search || undefined,
    includeInactive,
  });

  const deactivate = useDeactivateMdm();
  const reactivate = useReactivateMdm();
  const validate = useValidateMdmCredentials();
  const clearCache = useClearAmapiCache();

  async function handleValidate(mdm: MdmDto) {
    try {
      const result = await validate.mutateAsync(mdm.id);
      modal.info({
        title: `Validacion de credenciales - ${mdm.name}`,
        width: 600,
        content: (
          <div style={{ marginTop: 12 }}>
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              <ValidationRow ok={result.fileExists} label="Archivo JSON existe" />
              <ValidationRow ok={result.validJsonFormat} label="Formato JSON valido" />
              {result.serviceAccountEmail && (
                <Text>
                  <strong>Service Account:</strong> <Text code>{result.serviceAccountEmail}</Text>
                </Text>
              )}
              {result.projectIdInJson && (
                <Text>
                  <strong>Project ID en JSON:</strong> <Text code>{result.projectIdInJson}</Text>
                </Text>
              )}
              <ValidationRow
                ok={result.projectIdMatches}
                label={`Project ID coincide con el del MDM (${mdm.googleProjectId ?? '-'})`}
              />
              {result.errorMessage && (
                <Text type="danger">Error: {result.errorMessage}</Text>
              )}
            </Space>
          </div>
        ),
      });
    } catch (e: unknown) {
      const err = e as Error;
      message.error('Error: ' + err.message);
    }
  }

  async function handleDeactivate(mdm: MdmDto) {
    try {
      await deactivate.mutateAsync(mdm.id);
      message.success('MDM desactivado.');
    } catch (e: unknown) {
      const err = e as Error;
      message.error('Error: ' + err.message);
    }
  }

  async function handleReactivate(mdm: MdmDto) {
    try {
      await reactivate.mutateAsync(mdm.id);
      message.success('MDM reactivado.');
    } catch (e: unknown) {
      const err = e as Error;
      message.error('Error: ' + err.message);
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={3} style={{ margin: 0 }}>
          <ClusterOutlined style={{ marginRight: 12 }} />
          MDMs (tenants raiz)
        </Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditing(null); setFormOpen(true); }}>
          Nuevo MDM
        </Button>
      </div>

      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, gap: 8, flexWrap: 'wrap' }}>
          <Space>
            <Input
              prefix={<SearchOutlined />}
              placeholder="Buscar por nombre, codigo, project ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onPressEnter={() => setPage(1)}
              style={{ width: 320 }}
              allowClear
            />
            <Button onClick={() => { setPage(1); refetch(); }}>Buscar</Button>
            <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>Refrescar</Button>
          </Space>
          <Space>
            <Text>Incluir inactivos:</Text>
            <Switch checked={includeInactive} onChange={(v) => { setIncludeInactive(v); setPage(1); }} />
          </Space>
        </div>

        <Table<MdmDto>
          rowKey="id"
          loading={isLoading}
          dataSource={data?.items ?? []}
          pagination={{
            current: page,
            pageSize,
            total: data?.totalCount ?? 0,
            onChange: (p, ps) => { setPage(p); setPageSize(ps); },
            showSizeChanger: true,
            showTotal: (total) => `${total} en total`,
          }}
          columns={[
            {
              title: 'Nombre',
              key: 'name',
              render: (_, m) => (
                <Space direction="vertical" size={0}>
                  <Text strong>{m.name}</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    <Tag style={{ marginRight: 4 }}>{m.code}</Tag>
                    {m.description && <span>{m.description}</span>}
                  </Text>
                </Space>
              ),
            },
            {
              title: 'Google Cloud',
              key: 'gcp',
              render: (_, m) => (
                <Space direction="vertical" size={0}>
                  <Text>{m.googleProjectId ?? <Text type="secondary">(sin project)</Text>}</Text>
                  {m.googleCredentialsPath && (
                    <Tooltip title={m.googleCredentialsPath}>
                      <Tag
                        icon={m.credentialsFileExists ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />}
                        color={m.credentialsFileExists ? 'success' : 'error'}
                      >
                        {m.credentialsFileExists ? 'JSON OK' : 'JSON no existe'}
                      </Tag>
                    </Tooltip>
                  )}
                </Space>
              ),
            },
            {
              title: 'Stats',
              key: 'stats',
              width: 200,
              render: (_, m) => (
                <Space>
                  <Tooltip title="Enterprises activas">
                    <Badge count={m.enterprisesCount} showZero color="#1677ff" />
                  </Tooltip>
                  <Tooltip title="Devices activos">
                    <Badge count={m.devicesCount} showZero color="#52c41a" />
                  </Tooltip>
                  <Tooltip title="Usuarios con acceso">
                    <Badge count={m.usersCount} showZero color="#faad14" />
                  </Tooltip>
                </Space>
              ),
            },
            {
              title: 'Estado',
              dataIndex: 'isActive',
              width: 100,
              render: (active: boolean) =>
                active
                  ? <Tag color="success">Activo</Tag>
                  : <Tag color="default">Inactivo</Tag>,
            },
            {
              title: 'Creado',
              dataIndex: 'createdAt',
              width: 110,
              render: (v: string) => dayjs(v).format('DD/MM/YYYY'),
            },
            {
              title: 'Acciones',
              key: 'actions',
              width: 200,
              render: (_, m) => (
                <Space>
                  <Tooltip title="Limpiar cache AMAPI">
                    <Button
                      size="small"
                      icon={<ClearOutlined />}
                      onClick={async () => {
                        try {
                          await clearCache.mutateAsync();
                          message.success('Cache AMAPI limpiado.');
                        } catch {
                          message.error('Error al limpiar cache.');
                        }
                      }}
                      loading={clearCache.isPending}
                    />
                  </Tooltip>
                  <Tooltip title="Validar credenciales Google">
                    <Button
                      size="small"
                      icon={<SafetyCertificateOutlined />}
                      onClick={() => handleValidate(m)}
                      loading={validate.isPending}
                    />
                  </Tooltip>
                  <Tooltip title="Editar">
                    <Button
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => { setEditing(m); setFormOpen(true); }}
                    />
                  </Tooltip>
                  {m.isActive ? (
                    <Popconfirm title="Desactivar este MDM?" onConfirm={() => handleDeactivate(m)}>
                      <Button size="small" danger icon={<StopOutlined />} />
                    </Popconfirm>
                  ) : (
                    <Button size="small" type="primary" onClick={() => handleReactivate(m)}>
                      Reactivar
                    </Button>
                  )}
                </Space>
              ),
            },
          ]}
        />
      </Card>

      <MdmFormModal
        open={formOpen}
        mdm={editing}
        onClose={() => { setFormOpen(false); setEditing(null); }}
      />
    </div>
  );
}

function ValidationRow({ ok, label }: { ok: boolean; label: string }) {
  return (
    <Space>
      {ok
        ? <CheckCircleOutlined style={{ color: '#52c41a' }} />
        : <ExclamationCircleOutlined style={{ color: '#cf1322' }} />}
      <Text>{label}</Text>
    </Space>
  );
}
